import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuthStore } from "../../stores/authStore";
import type { LoginResponse } from "../../types/api";
import { chaves } from "../chamados/gestao/api";
import { lembrarContrato } from "./sessao";

/**
 * Um painel da equipe (09/10/2026): um contrato que abre chamado e de cuja
 * equipe a pessoa faz parte. Dois contratos do mesmo cliente são dois painéis.
 */
export interface ContratoAtendido {
  idContrato: number;
  idCliente: number;
  cliente: string;
  contrato: string;
  abertos: number;
  total: number;
}

export interface OpcaoStatus {
  id: number;
  nome: string;
}

/** Troca o id_token da Microsoft pela sessão do portal, ainda sem contrato. */
export const entrarComTokenMicrosoft = async (idToken: string) => {
  const { data } = await api.post<LoginResponse>("/auth/microsoft", { idToken });
  return data;
};

/**
 * Sessão nova, escopada no contrato escolhido. Contrato e cliente vão dentro
 * do token assinado pela API, e não num header: toda rota lê o painel do
 * token, e a API só devolve os chamados dele.
 */
export const escolherContrato = async (idContrato: number) => {
  const { data } = await api.post<LoginResponse>("/equipe/contrato", { idContrato });
  return data;
};

/** Os contratos da equipe da pessoa que abrem chamado, os com chamado em aberto primeiro. */
export const useContratosAtendidos = () =>
  useQuery({
    queryKey: ["equipe-contratos"],
    queryFn: async (): Promise<ContratoAtendido[]> => {
      const { data } = await api.get<ContratoAtendido[]>("/equipe/contratos");
      return data;
    },
    staleTime: 60_000,
  });

/**
 * Passa a atender outro contrato. As consultas da tela não levam o contrato na
 * chave — para o cliente ele nunca muda —, então o cache inteiro é descartado:
 * nada do painel anterior pode sobrar na tela de quem está respondendo.
 */
export const useAtenderContrato = () => {
  const clienteQuery = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  return useMutation({
    mutationFn: escolherContrato,
    onSuccess: (sessao) => {
      clienteQuery.clear();
      setUser(sessao);
      lembrarContrato(sessao.email, sessao.idContrato);
    },
  });
};

/** Os status que a equipe pode escolher: os ativos, menos Reaberto. */
export const useStatusEquipe = (habilitado: boolean) =>
  useQuery({
    queryKey: ["equipe-status"],
    queryFn: async (): Promise<OpcaoStatus[]> => {
      const { data } = await api.get<OpcaoStatus[]>("/equipe/status");
      return data;
    },
    enabled: habilitado,
    staleTime: 10 * 60_000,
  });

export interface MudancaDeStatus {
  idChamado: number;
  idStatus: number;
  observacao?: string;
}

/**
 * Muda o status pela equipe. A API grava a linha do tempo ("O status do
 * chamado foi alterado para ...", com a observação) e avisa o contato por
 * e-mail.
 */
export const useMudarStatus = () => {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: async ({ idChamado, idStatus, observacao }: MudancaDeStatus) => {
      await api.post(`/portal-cliente/chamados/${idChamado}/status`, {
        idStatus,
        observacao: observacao?.trim() || null,
      });
    },
    onSettled: (_dados, _erro, { idChamado }) => {
      // Também no erro: a recusa pode ser por status que mudou nesse meio tempo.
      clienteQuery.invalidateQueries({ queryKey: chaves.detalhe(idChamado) });
      clienteQuery.invalidateQueries({ queryKey: ["gestao-chamados"] });
      // O quadro também: a etiqueta de status do cartão muda por aqui.
      clienteQuery.invalidateQueries({ queryKey: ["chamados", "kanban"] });
      clienteQuery.invalidateQueries({ queryKey: ["chamado", idChamado] });
    },
  });
};
